import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * KobuFooter — editorial closing band in the Kobu system. Parchment canvas,
 * obsidian hairline top border, Fira Mono labels, no gold, no shadow. The
 * Dispatch newsletter handler is preserved verbatim from SiteFooter
 * (Firestore newsletter_subscribers write, dedupe, status states).
 */

const KobuFooter: React.FC = () => {
  const [dispatchEmail, setDispatchEmail] = useState('');
  const [dispatchStatus, setDispatchStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dispatchEmail.trim()) return;
    setDispatchStatus('sending');
    try {
      const { db } = await import('../../services/firebase');
      const { collection, addDoc, query, where, getDocs, limit, serverTimestamp } = await import('firebase/firestore');
      if (!db) throw new Error('Firestore unavailable');
      const subscribersRef = collection(db, 'newsletter_subscribers');
      const existing = await getDocs(query(subscribersRef, where('email', '==', dispatchEmail.trim().toLowerCase()), limit(1)));
      if (!existing.empty) { setDispatchStatus('success'); setDispatchEmail(''); return; }
      await addDoc(subscribersRef, {
        email: dispatchEmail.trim().toLowerCase(),
        source: 'footer',
        subscribedAt: serverTimestamp(),
      });
      setDispatchStatus('success');
      setDispatchEmail('');
    } catch {
      setDispatchStatus('error');
    }
  };

  const linkClasses = 'kobu-caption text-obsidian hover:text-ash-gray transition-colors';
  const colHead = 'kobu-label text-ash-gray mb-6 block';

  return (
    <footer className="bg-parchment border-t border-obsidian py-[60px] px-6 sm:px-10">
      <div className="max-w-[1440px] mx-auto">

        {/* The Dispatch */}
        <div className="border-b border-obsidian pb-[50px] mb-[50px]">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div className="max-w-sm">
              <h4 className="kobu-heading text-[24px] mb-2">The Dispatch</h4>
              <p className="kobu-body text-[15px]">
                Curated destination intelligence and insider travel insights.
              </p>
            </div>
            {dispatchStatus === 'success' ? (
              <p className="kobu-label text-obsidian">Welcome to The Dispatch.</p>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                <input
                  type="email"
                  name="email"
                  value={dispatchEmail}
                  onChange={(e) => setDispatchEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  aria-label="Email address for The Dispatch newsletter"
                  className="flex-1 md:w-56 px-5 py-3 bg-gallery-white border border-obsidian text-obsidian text-sm placeholder:text-ash-gray focus:outline-none"
                  style={{ fontFamily: 'var(--font-gill)' }}
                />
                <button
                  type="submit"
                  disabled={dispatchStatus === 'sending'}
                  className="kobu-label bg-obsidian text-parchment px-6 py-3 hover:bg-charcoal-deep transition-colors disabled:opacity-50 whitespace-nowrap min-h-[44px]"
                >
                  {dispatchStatus === 'sending' ? '…' : 'Subscribe'}
                </button>
              </form>
            )}
          </div>
          {dispatchStatus === 'error' && (
            <p className="mt-3 kobu-caption text-obsidian text-right">Something went wrong. Please try again.</p>
          )}
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-12 mb-[50px]">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="kobu-label text-obsidian block mb-4">Aura Traveler</Link>
            <p className="kobu-body text-[14px] max-w-[200px]">
              Intelligent luxury travel design for agencies, hotels, and discerning travelers.
            </p>
          </div>

          <div>
            <span className={colHead}>Product</span>
            <ul className="space-y-4">
              <li><Link to="/about" className={linkClasses}>About</Link></li>
              <li><Link to="/philosophy" className={linkClasses}>Philosophy</Link></li>
              <li><Link to="/pricing" className={linkClasses}>Pricing</Link></li>
              <li><Link to="/journal" className={linkClasses}>Journal</Link></li>
              <li><Link to="/field-notes" className={linkClasses}>Field Notes</Link></li>
              <li><Link to="/faq" className={linkClasses}>FAQ</Link></li>
              <li><Link to="/day-planner" className={linkClasses}>Day Planner</Link></li>
              <li><Link to="/explore" className={linkClasses}>Explore</Link></li>
            </ul>
          </div>

          <div>
            <span className={colHead}>Solutions</span>
            <ul className="space-y-4">
              <li><Link to="/for-agencies" className={linkClasses}>For Agencies</Link></li>
              <li><Link to="/for-hotels" className={linkClasses}>For Hotels</Link></li>
              <li><Link to="/contact" className={linkClasses}>Contact</Link></li>
            </ul>
          </div>

          <div>
            <span className={colHead}>Legal</span>
            <ul className="space-y-4">
              <li><a href="/privacy.html" target="_blank" rel="noopener noreferrer" className={linkClasses}>Privacy Policy</a></li>
              <li><a href="/terms.html" target="_blank" rel="noopener noreferrer" className={linkClasses}>Terms of Service</a></li>
            </ul>
          </div>

          <div>
            <span className={colHead}>Follow</span>
            <ul className="space-y-4">
              <li><a href="https://instagram.com/auratraveler_com" target="_blank" rel="noopener noreferrer" className={linkClasses}>Instagram</a></li>
              <li><a href="https://x.com/AuraTraveler" target="_blank" rel="noopener noreferrer" className={linkClasses}>X / Twitter</a></li>
              <li><a href="https://linkedin.com/company/auratraveler" target="_blank" rel="noopener noreferrer" className={linkClasses}>LinkedIn</a></li>
              <li><a href="https://facebook.com/auratraveler" target="_blank" rel="noopener noreferrer" className={linkClasses}>Facebook</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-obsidian pt-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="kobu-caption text-ash-gray">&copy; 2026 Aura Traveler — Aura Engine v3.1</p>
          <p className="kobu-caption text-ash-gray">Crafted for the discerning traveler</p>
        </div>
      </div>
    </footer>
  );
};

export default KobuFooter;
